import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { List, ListItem, Checkbox, ListItemText, IconButton, Box, Divider } from '@mui/material';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import WsColors from '../../WsColors';
import { WsButton } from '../WsButton';
import { SearchBarNew, SearchBarVariant } from '../WsTextSearch';
import { WsTypography, WsTypoVariants } from '../WsTypography';

export type FieldOption = {
  id: string;
  label?: string;
  checked: boolean;
  required?: boolean;
};

const SortableItem = ({
  option,
  onToggle,
}: {
  option: FieldOption;
  onToggle: (id: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 0,
      }}
    >
      <IconButton {...listeners} {...attributes} sx={{ color: WsColors.GenericRichBlack, mr: -1 }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Checkbox
        checked={option.checked}
        disabled={option.required}
        onChange={() => onToggle(option.id)}
      />
      <ListItemText
        primary={
          <WsTypography variant={WsTypoVariants.BODY1}>
            {option.label || option.id}
            {option.required && <span style={{ color: WsColors.Red }}> *</span>}
          </WsTypography>
        }
      />
    </ListItem>
  );
};

export interface WsFieldSelectorProps {
  initialOptions: FieldOption[];
  onApply: (options: FieldOption[]) => void;
  onReset: () => void;
  searchable?: boolean;
}

const WsFieldSelector: React.FC<WsFieldSelectorProps> = ({
  initialOptions,
  searchable,
  onApply,
  onReset,
}) => {
  const [options, setOptions] = useState<FieldOption[]>(initialOptions);
  const sensors = useSensors(useSensor(PointerSensor));
  const formMethods = useForm();
  const searchedItem = formMethods.watch('item', '')?.toLowerCase() ?? '';
  const filteredOptions = options.filter((option) =>
    (option.label || option.id).toLowerCase().includes(searchedItem)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = options.findIndex((item) => item.id === active.id);
      const newIndex = options.findIndex((item) => item.id === over.id);
      setOptions((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleToggle = (id: string) => {
    setOptions((prev) =>
      prev.map((item) => (item.id == id ? { ...item, checked: !item.checked } : item))
    );
  };

  return (
    <Box sx={{ width: 300, p: 1.5 }}>
      {searchable && (
        <Box overflow="hidden">
          <FormProvider {...formMethods}>
            <SearchBarNew variant={SearchBarVariant.WITHOUT_SEARCH_BTN} size="small" name="item" />
          </FormProvider>
        </Box>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
          <List dense sx={{ height: 300, overflow: 'scroll' }}>
            {filteredOptions.map((option) => (
              <SortableItem key={option.id} option={option} onToggle={handleToggle} />
            ))}
          </List>
        </SortableContext>
      </DndContext>
      <Divider sx={{ my: 2 }} />
      <Box display="flex" justifyContent="space-between">
        <WsButton variant="contained" size="small" onClick={() => onApply(options)}>
          Apply
        </WsButton>
        <WsButton variant="outlined" size="small" onClick={() => onReset()}>
          Reset to Default
        </WsButton>
      </Box>
    </Box>
  );
};

export default WsFieldSelector;
